<script>
import {hasEditPermission,currentRoleLevelName,trialActiveForClients} from '@/mixins/roleLevelPermissions';
import { hasStatusAgreementRoute } from '@/lib/agreement/relationshipFacts';
import { mapState } from 'vuex';


export default {
	props: ['agreementData'],
	data(){
		return{
			isViewMode : !hasEditPermission('section-basics'),
			showLearnButton: ['client-level-1', 'client-level-2'].includes(currentRoleLevelName)
		}
	},
	computed: {
		...mapState(['currentRoleLevel']),
		legalHistory() {
			return this.agreementData.legalHistory;
		},
		relationship() {
			return this.agreementData.relationship;
		},
		province() {
			return this.agreementData.agreement.updatedProvinceOfAgreement;
		},
		// Other provinces reach this section only for the status agreement below.
		showCourtHistory() {
			return this.province === 'British Columbia';
		},
		// Creates status regardless of duration — not the cohabitation agreement below.
		showStatusAgreement() {
			return hasStatusAgreementRoute(this.province) && this.relationship.married !== true;
		},
		statusAgreementLabel() {
			return {
				Alberta: 'Did the parties enter into an Adult Interdependent Partner Agreement?',
				Manitoba: 'Did the parties register their common-law relationship?',
				'Nova Scotia': 'Did the parties enter into a Domestic Partner Declaration?',
			}[this.province];
		},
		filteredOptions(){
			const options=[
					'Parenting',
					'Interim Child Support',
					'Interim Spousal Support',
					'Interim occupancy of the Family Residence',
					'Interim financial management for each of the parties',
				]
			return options.filter((option)=>{
				if(option === 'Parenting'){
					return this.agreementData.agreement.sections.includes('Parenting') && this.agreementData.children.length > 0
				}
				if(option === 'Interim Child Support'){
					return this.agreementData.agreement.sections.includes('Child Support') && this.agreementData.children.length > 0
				}
				if(option === 'Interim Spousal Support'){
					return this.agreementData.agreement.sections.includes('Spousal Support')
				}
				if(option === 'Interim occupancy of the Family Residence'){
					return this.agreementData.agreement.propertyRecitals.includes('Family Residence')
				}
				return true
			})
		},
		clientUserAgreement(){
			return trialActiveForClients() || currentRoleLevelName === 'client-sharing-user';
		},
		isUserSeparatingParty(){
			return this.currentRoleLevel.roleAssociated === 'party'
		},
		dynamicQuestions(){
			if(this.isUserSeparatingParty){
				return {
					provincialCourtCity: 'In what city did you register the court file?',
					supremeCourtCity: 'In what city did you register the court file?'
				}
			}
			else{
				return {
					provincialCourtCity: 'What city were the court proceedings registered in?',
					supremeCourtCity: 'What city were the court proceedings registered in?',
				}
			}
		}
	},
};
</script>

<template>
	<b-card v-if="!clientUserAgreement">
		<div slot="header" class="section-heading">
			<h4>Legal History of Parties</h4>
			<a href="#" onclick="Intercom('showArticle', 12885507)" v-if="this.showLearnButton">Learn &nbsp;<b-icon-book/></a>
		</div>

		<conditional-group class="mb-3" :condition="showStatusAgreement">
			<yes-no-choice
				v-model="legalHistory.statusAgreement"
				reset-undefined
				:label="statusAgreementLabel"
				class="mb-3"
				:testid="'legal-history-status-agreement'"
				:disabled="isViewMode"
				toolTipText="This is different from a Cohabitation Agreement. Registering the
				relationship, or signing this agreement, gives the parties the status itself,
				no matter how long they have lived together."
			/>
		</conditional-group>

		<conditional-group class="mb-3" :condition="legalHistory.statusAgreement">
			<date-input
				v-model="legalHistory.statusAgreementDate"
				reset-undefined
				label="Date of Agreement"
				class="mb-3"
				:testid="'legal-history-status-agreement-date'"
				:disabled="isViewMode"
			/>
		</conditional-group>

		<template v-if="showCourtHistory">

		<conditional-group
			class="mb-3"
			:condition="relationship.cohabited"
		>
			<yes-no-choice
				v-model="legalHistory.cohabitationAgreement"
				reset-undefined
				label="Did the parties enter into a Cohabitation Agreement?"
				class="mb-3"
				:testid="'legal-history-cohabitation-agreement'"
				:disabled="isViewMode"
			/>
		</conditional-group>

		<conditional-group
			class="mb-3"
			:condition="legalHistory.cohabitationAgreement"
		>
			<date-input
				v-model="legalHistory.cohabitationAgreementDate"
				reset-undefined
				label="Date of Agreement"
				class="mb-3"
				:testid="'legal-history-agreement-date'"
				:disabled="isViewMode"
			/>
		</conditional-group>

		<conditional-group
			class="mb-3"
			:condition="relationship.married"
		>
			<yes-no-choice
				v-if="relationship.married"
				v-model="legalHistory.marriageAgreement"
				reset-undefined
				label="Did the parties enter into a Marriage Agreement?"
				class="mb-3"
				:testid="'legal-history-marriage-agreement'"
				:disabled="isViewMode"
			/>
		</conditional-group>

		<conditional-group
			class="mb-3"
			:condition="legalHistory.marriageAgreement"
		>
			<date-input
				v-if="relationship.married"
				v-model="legalHistory.marriageAgreementDate"
				reset-undefined
				label="Date of Agreement"
				class="mb-3"
				:testid="'legal-history-date'"
				:disabled="isViewMode"
			/>
		</conditional-group>

		<yes-no-choice
			v-if="!isUserSeparatingParty"
			v-model="legalHistory.interimAgreement"
			reset-undefined
			label="Did the parties enter into an Interim Separation Agreement?"
			class="mb-3"
			:testid="'legal-history-interim-separation-agreement'"
			:disabled="isViewMode"
		/>

		<conditional-group
			class="mb-3"
			:condition="legalHistory.interimAgreement"
		>
			<radio-group
				v-model="legalHistory.interimAgreementTreatment"
				reset-undefined
				select
				class="mb-3"
				input-cols="4"
				label="How will this agreement affect the previous agreement?"
				:options="[
					{ text: 'Replace', value: 'replaces' },
					{ text: 'Vary', value: 'varies' },
					{ text: 'Supplement', value: 'supplements' },
				]"
				:testid="'legal-history-previous-agreement-effect'"
				:length="'long'"
				:disabled="isViewMode"
			/>

			<date-input
				v-model="legalHistory.interimAgreementDate"
				reset-undefined
				label="Date of Agreement"
				class="mb-3"
				:testid="'legal-history-interim-agreement-date'"
				:disabled="isViewMode"
			/>

			<check-group
				v-model="legalHistory.interimAgreementContents"
				reset-undefined
				class="mb-3"
				label="What matters were included in the past interim separation agreement?"
				:options="filteredOptions"
				:testid="'legal-history-interim-agreement-contents'"
				:id="'legal-history-interim-agreement-contents'"
				:disabled="isViewMode"
			/>

			<conditional-group
				class="mb-3"
				:condition="
					legalHistory.interimAgreementContents &&
					legalHistory.interimAgreementContents.includes(
						'Interim Spousal Support'
					)
				"
			>
				<radio-group
					v-model="
						legalHistory.interimAgreementSpousalSupportRecipient
					"
					input-cols="4"
					partner
					select
					class="mb-3"
					reset-undefined
					label="The interim separation agreement provided support for:"
					:agreement-data="agreementData"
					:testid="'legal-history-interim-support'"
					:length="'long'"
					:disabled="isViewMode"
				/>
			</conditional-group>
		</conditional-group>

		<yes-no-choice
			v-model="legalHistory.provincialCourt"
			reset-undefined
			label="Have court proceedings commenced in Provincial Court?"
			class="mb-3"
			:testid="'legal-history-provincial-court-cmmencement'"
			:disabled="isViewMode"
		/>

		<conditional-group
			class="mb-3"
			:condition="legalHistory.provincialCourt"
		>
			<!-- <radio-group
				v-model="legalHistory.provincialCourtApplicant"
				reset-undefined
				select
				partner
				input-cols="4"
				class="mb-3"
				label="Which party filed the Provincial Court documents?"
				:agreement-data="agreementData"
				:testid="'legal-history-provincial-court-documents'"
			/> -->

			<!-- <radio-group
				v-model="legalHistory.provincialCourtCommencedDocument"
				reset-undefined
				input-cols="6"
				class="mb-3"
				label="What document commenced action?"
				other
				select
				:options="[
					{
						text: 'Application to Obtain an Order',
						value: 'applicationToObtainOrder',
					},
					{ text: 'Consent Order', value: 'consentOrder' },
					{ text: 'Agreement', value: 'agreement' },
				]"
				:testid="'legal-history-document-commenced-action'"
			/> -->

			<input-group
				v-model="legalHistory.provincialCourtCity"
				reset-undefined
				input-cols="4"
				:label="dynamicQuestions.provincialCourtCity"
				class="mb-3"
				placeholder="ie: Victoria"
				:testid="'legal-history-provincial-court-city'"
				:disabled="isViewMode"
				toolTipText="The registry can be found on your court documents along with the Court file number."
			/>

			<input-group
				v-model="legalHistory.provincialCourtFileNumber"
				reset-undefined
				input-cols="4"
				label="Provincial Court File Number"
				class="mb-3"
				:testid="'legal-history-court-file-name'"
				:disabled="isViewMode"
			/>

			<!-- <radio-group
				v-model="legalHistory.provincialCourtRespondingDocument"
				reset-undefined
				input-cols="4"
				label="Responding Document"
				select
				class="mb-3"
				other
				:options="[{ text: 'Reply', value: 'Reply' }]"
				:testid="'legal-history-court-responding-document'"
			/> -->
		</conditional-group>

		<yes-no-choice
			v-model="legalHistory.supremeCourt"
			reset-undefined
			label="Have court proceedings commenced in Supreme Court?"
			class="mb-3"
			:testid="'legal-history-supreme-court-commencement'"
			:disabled="isViewMode"
		/>

		<conditional-group class="mb-3" :condition="legalHistory.supremeCourt">
			<!-- <radio-group
				v-model="legalHistory.supremeCourtApplicant"
				select
				partner
				input-cols="4"
				reset-undefined
				class="mb-3"
				label="Which party filed the Supreme Court documents?"
				:agreement-data="agreementData"
				:testid="'legal-history-supreme-court-documents-filed-by'"
			/> -->

			<!-- <radio-group
				v-model="legalHistory.supremeCourtCommencedDocument"
				reset-undefined
				input-cols="8"
				class="mb-3"
				label="What document commenced action?"
				other
				select
				:options="[
					{
						text: 'Notice of Family Claim',
						value: 'noticeOfFamilyClaim',
					},
					{
						text: 'Writ of Summons and Statement of Claim',
						value: 'statementOfClaim',
					},
					{ text: 'Consent Order', value: 'consentOrder' },
				]"
				:testid="'legal-history-supreme-court-commenced-action'"
			/> -->

			<input-group
				v-model="legalHistory.supremeCourtCity"
				reset-undefined
				input-cols="4"
				:label="dynamicQuestions.supremeCourtCity"
				class="mb-3"
				placeholder="ie: Victoria"
				:testid="'legal-history-supreme-court-city'"
				:disabled="isViewMode"
				toolTipText="The registry can be found on your court documents along with the Court file number."
			/>

			<input-group
				v-model="legalHistory.supremeCourtFileNumber"
				reset-undefined
				input-cols="4"
				label="Supreme Court File Number"
				class="mb-3"
				:testid="'legal-history-supreme-court-file-number'"
				:disabled="isViewMode"
			/>

			<!-- <radio-group
				v-model="legalHistory.supremeCourtRespondingDocument"
				reset-undefined
				input-cols="8"
				label="Responding Document"
				select
				class="mb-3"
				other
				:options="[
					{
						text: 'Response to Family Claim',
						value: 'responseToFamilyClaim',
					},
					{
						text: 'Appearance and Response',
						value: 'appearanceAndResponse',
					},
					{
						text: 'Appearance, Response and Counterclaim',
						value: 'appearanceReponseAndCounterclaim',
					},
				]"
				:testid="'legal-history-supreme-court-responding-document'"
			/> -->
		</conditional-group>
		<additional-clauses v-model="legalHistory.additionalClauses" :testid="'legal-history-add-clause-'"  :disabled="isViewMode"/>
		</template>
	</b-card>
</template>
